import { useEffect, useState } from 'react';
import MaintenancePanel from '../components/MaintenancePanel';
import IncidentList from '../components/IncidentList';
import LoadingSpinner from '../components/LoadingSpinner';
import ServiceStatusList from '../components/ServiceStatusList';
import HistoryPage from '../components/HistoryPage';
import { getFirestoreStatus, getClusters, getActiveMaintenance, getArchivedMaintenance, getActiveIncidents, getArchivedIncidents } from '../services/api';
import { sortIncidents, sortMaintenances } from '../services/sorting';
import { regionMapping } from '../services/region_mapping';
import { CheckCircle, AlertTriangle, XCircle, MinusCircle, Plus, Minus, HelpCircle } from "lucide-react";

const ClusterCard = ({ cluster, onToggle, isSelected, serviceStatuses }) => {
	const toggleCard = () => {
		onToggle(cluster.name);
	};

  const statusIcons = {
    operational: <CheckCircle className="w-5 h-5 text-green-600" />,
    degraded: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
    outage: <XCircle className="w-5 h-5 text-red-600" />,
    not_applicable: <MinusCircle className="w-5 h-5 text-gray-500" />,
  };

  const statusIcon = statusIcons[cluster.overall_status] || <MinusCircle className="w-5 h-5 text-gray-500" />;

  return (
    <div className="border-b border-gray-200">
      <div
        className="bg-white p-4 flex justify-between items-center cursor-pointer"
        onClick={toggleCard}
      >
        <div className="flex items-center">
          {isSelected ? <Minus className="w-5 h-5 text-gray-500" /> : <Plus className="w-5 h-5 text-gray-500" />}
          <h2 className="ml-3 text-base font-bold text-gray-900">{cluster.customer_alias || cluster.name}</h2>
          <div className="relative group ml-2">
            <HelpCircle className="w-4 h-4 text-gray-500" />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity">
              {regionMapping[cluster.region] || cluster.region}
             </div>
            </div>
           </div>
           <div className="flex items-center">
          {statusIcon}
          <span className="ml-3 text-base font-normal text-gray-700 capitalize">
            {cluster.overall_status ? cluster.overall_status.replace(/_/g, ' ') : 'Not Available'}
          </span>
        </div>
      </div>
      {isSelected && (
        <div className="p-4 bg-white">
          <ServiceStatusList serviceStatuses={serviceStatuses} />
        </div>
      )}
    </div>
  );
};

function App() {
  const [clusters, setClusters] = useState([]);
  const [expandedClusters, setExpandedClusters] = useState([]);
  const [allIncidents, setAllIncidents] = useState([]);
  const [clusterServiceStatuses, setClusterServiceStatuses] = useState({});
  const [allPastIncidents, setAllPastIncidents] = useState([]);
  const [activeMaintenance, setActiveMaintenance] = useState(null);
  const [archivedMaintenance, setArchivedMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clusterAliasMap, setClusterAliasMap] = useState({});
  const [page, setPage] = useState(window.location.pathname === '/history' ? 'history' : 'main');

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setPage(path === '/history' ? 'history' : 'main');
  };

  useEffect(() => {
    const handlePopState = () => {
      setPage(window.location.pathname === '/history' ? 'history' : 'main');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleToggleCluster = (clusterName) => {
    setExpandedClusters(prevExpanded => {
      const isExpanded = prevExpanded.includes(clusterName);
      if (isExpanded) {
        return prevExpanded.filter(name => name !== clusterName);
      } else {
        return [...prevExpanded, clusterName];
      }
    });
  };

   useEffect(() => {
    Promise.all([
      getClusters(),
      getActiveMaintenance(),
      getArchivedMaintenance(),
      getActiveIncidents(),
      getArchivedIncidents(),
    ]).then(([clustersRes, maintenanceRes, archivedMaintenanceRes, activeIncidentsRes, archivedIncidentsRes]) => {
      const clusterData = clustersRes.data || [];
      setClusters(clusterData);
      setActiveMaintenance(maintenanceRes.data?.id ? maintenanceRes.data : null);
      setArchivedMaintenance(sortMaintenances(archivedMaintenanceRes.data));
      setAllIncidents(sortIncidents(activeIncidentsRes.data || []));
      setAllPastIncidents(sortIncidents(archivedIncidentsRes.data || []));

      // Fetch per-cluster status for overall_status, region, service_statuses
      const statusPromises = clusterData.map(cluster =>
        getFirestoreStatus(cluster.name)
          .then(statusRes => {
            const data = statusRes.data || {};
            setClusters(prevClusters => prevClusters.map(c =>
              c.name === cluster.name
                ? { ...c, overall_status: data.overall_status, region: data.region, customer_alias: data.customer_alias }
                : c
            ));
            return {
              clusterName: cluster.name,
              serviceStatuses: data.service_statuses || {},
              customerAlias: data.customer_alias || '',
            };
          })
          .catch(err => {
            console.error(`Failed to fetch status for ${cluster.name}`, err);
            return { clusterName: cluster.name, serviceStatuses: {} };
          })
      );

      Promise.all(statusPromises).then(results => {
        const serviceStatusMap = {};
        const aliasMap = {};
        results.forEach(r => {
          serviceStatusMap[r.clusterName] = r.serviceStatuses;
          if (r.customerAlias) {
            aliasMap[r.clusterName] = r.customerAlias;
          }
        });
        setClusterServiceStatuses(serviceStatusMap);
        setClusterAliasMap(aliasMap);
        setLoading(false);
      });
    });
  }, []);

  useEffect(() => {
    expandedClusters.forEach(clusterName => {
      if (!clusterServiceStatuses[clusterName]) {
        getFirestoreStatus(clusterName)
          .then(statusRes => {
            const data = statusRes.data || {};
            setClusterServiceStatuses(prev => ({ ...prev, [clusterName]: data.service_statuses || {} }));
          })
          .catch(err => {
            console.error(`Failed to fetch service statuses for ${clusterName}`, err);
          });
      }
    });
  }, [expandedClusters, clusterServiceStatuses]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (page === 'history') {
    return (
      <HistoryPage
        pastIncidents={allPastIncidents}
        archivedMaintenance={archivedMaintenance}
        onBack={() => navigate('/')}
        clusterAliasMap={clusterAliasMap}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="py-8">
        <div className="max-w-3xl mx-auto px-4 flex justify-between items-center">
          <img src="/cv_logo.png" alt="Logo" className="h-12" />
          <img src="/agni-blue.png" alt="AGNI Status Page" className="h-[74px]" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4">
        {activeMaintenance && (
          <div className="mx-auto my-4">
            <MaintenancePanel maintenance={activeMaintenance} clusterAliasMap={clusterAliasMap} />
          </div>
        )}
         <div className="border-t border-l border-r border-gray-200" style={{ height: 'auto' }}>
          {clusters.map(cluster => (
            <ClusterCard
              key={cluster.name}
              cluster={cluster}
              onToggle={handleToggleCluster}
              isSelected={expandedClusters.includes(cluster.name)}
              serviceStatuses={clusterServiceStatuses[cluster.name] || {}}
            />
          ))}
        </div>
      </div>

      <div id="incidents" className="max-w-6xl mx-auto mt-8 px-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Incidents</h2>
        <hr className="border-t border-gray-200 mb-4" />
        <IncidentList incidents={allIncidents} clusterAliasMap={clusterAliasMap} />

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/history')}
            className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
          >
            View Incident &amp; Notice History →
          </button>
        </div>

        <div className="text-center text-sm text-gray-400 mt-8 mb-4">
          Subscription features for email notifications and status alerts will be available soon.
        </div>
      </div>
    </div>
  );
}

export default App;
